export type WeekdayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface OpeningTimeSlot {
  open: string;
  close: string;
}

export type OpeningHours = Record<WeekdayKey, OpeningTimeSlot[]>;

export const WEEKDAY_DEFINITIONS: ReadonlyArray<{ key: WeekdayKey; label: string; short: string }> = [
  { key: 'monday', label: 'Lundi', short: 'Lun' },
  { key: 'tuesday', label: 'Mardi', short: 'Mar' },
  { key: 'wednesday', label: 'Mercredi', short: 'Mer' },
  { key: 'thursday', label: 'Jeudi', short: 'Jeu' },
  { key: 'friday', label: 'Vendredi', short: 'Ven' },
  { key: 'saturday', label: 'Samedi', short: 'Sam' },
  { key: 'sunday', label: 'Dimanche', short: 'Dim' },
];

export const MAX_OPENING_SLOTS_PER_DAY = 6;

export function emptyOpeningHours(): OpeningHours {
  return WEEKDAY_DEFINITIONS.reduce((acc, { key }) => {
    acc[key] = [];
    return acc;
  }, {} as OpeningHours);
}

function isWeekdayKey(value: string): value is WeekdayKey {
  return WEEKDAY_DEFINITIONS.some((day) => day.key === value);
}

/** Accepte un objet horaires, une chaîne JSON, ou un lieu avec opening_hours / openingHours. */
export function coerceOpeningHoursInput(raw: unknown): unknown {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      return null;
    }
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw;
  }

  const record = raw as Record<string, unknown>;
  if ('opening_hours' in record) return record.opening_hours;
  if ('openingHours' in record) return record.openingHours;

  return raw;
}

export function readOpeningHoursFromPlace(
  place: { opening_hours?: unknown; openingHours?: unknown } | null | undefined,
): unknown {
  if (!place) return null;
  if (place.opening_hours !== undefined && place.opening_hours !== null) {
    return coerceOpeningHoursInput(place.opening_hours);
  }
  if (place.openingHours !== undefined && place.openingHours !== null) {
    return coerceOpeningHoursInput(place.openingHours);
  }
  return null;
}

function normalizeTime(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  if (!match) return null;
  const hour = Number.parseInt(match[1], 10);
  const minute = Number.parseInt(match[2], 10);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function isOvernightOpeningSlot(slot: OpeningTimeSlot): boolean {
  return slot.open > slot.close;
}

function isValidOpeningSlot(open: string, close: string): boolean {
  return open !== close;
}

export function normalizeOpeningHours(raw: unknown): OpeningHours {
  const base = emptyOpeningHours();
  const coerced = coerceOpeningHoursInput(raw);
  if (!coerced || typeof coerced !== 'object' || Array.isArray(coerced)) {
    return base;
  }

  for (const [day, slots] of Object.entries(coerced as Record<string, unknown>)) {
    if (!isWeekdayKey(day) || !Array.isArray(slots)) continue;
    const normalizedSlots: OpeningTimeSlot[] = [];
    for (const slot of slots.slice(0, MAX_OPENING_SLOTS_PER_DAY)) {
      if (!slot || typeof slot !== 'object') continue;
      const open = normalizeTime((slot as OpeningTimeSlot).open);
      const close = normalizeTime((slot as OpeningTimeSlot).close);
      if (!open || !close || !isValidOpeningSlot(open, close)) continue;
      normalizedSlots.push({ open, close });
    }
    normalizedSlots.sort((a, b) => a.open.localeCompare(b.open));
    base[day] = normalizedSlots;
  }

  return base;
}

export function formatOpeningHoursSlot(slot: OpeningTimeSlot): string {
  const overnightSuffix = isOvernightOpeningSlot(slot) ? ' (lendemain)' : '';
  return `${slot.open} – ${slot.close}${overnightSuffix}`;
}

export function formatOpeningHoursDay(slots: OpeningTimeSlot[]): string {
  if (!slots.length) return 'Fermé';
  return slots.map(formatOpeningHoursSlot).join(', ');
}

export function hasConfiguredOpeningHours(raw: unknown): boolean {
  const hours = normalizeOpeningHours(raw);
  return WEEKDAY_DEFINITIONS.some(({ key }) => (hours[key] ?? []).length > 0);
}

export function openingHoursSignature(raw: unknown): string {
  return JSON.stringify(normalizeOpeningHours(raw));
}

export function formatOpeningHoursSummary(hours: OpeningHours): string {
  const parts = WEEKDAY_DEFINITIONS.map(({ key, short }) => {
    const slots = hours[key] ?? [];
    if (!slots.length) return null;
    return `${short} ${formatOpeningHoursDay(slots)}`;
  }).filter(Boolean);

  if (!parts.length) return 'Horaires non renseignés';
  if (parts.length <= 2) return parts.join(' · ');
  return `${parts.slice(0, 2).join(' · ')} · …`;
}

export function validateOpeningHours(hours: OpeningHours): string | null {
  for (const { key, label } of WEEKDAY_DEFINITIONS) {
    const slots = hours[key] ?? [];
    if (slots.length > MAX_OPENING_SLOTS_PER_DAY) {
      return `Maximum ${MAX_OPENING_SLOTS_PER_DAY} plages pour ${label}.`;
    }
    for (let i = 0; i < slots.length; i += 1) {
      const slot = slots[i];
      const open = normalizeTime(slot.open);
      const close = normalizeTime(slot.close);
      if (!open || !close) {
        return `Heures invalides (${label}, plage ${i + 1}).`;
      }
      if (open === close) {
        return `L'ouverture et la fermeture ne peuvent pas être identiques (${label}, plage ${i + 1}).`;
      }
    }
  }
  return null;
}

export function copyWeekdayHours(source: OpeningHours, from: WeekdayKey, to: WeekdayKey): OpeningHours {
  return {
    ...source,
    [to]: source[from].map((slot) => ({ ...slot })),
  };
}
